import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Youtube, Instagram, Facebook, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import usePageContent from '@/hooks/usePageContent';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t: c } = usePageContent('footer');
  const [socialLinks, setSocialLinks] = useState({
    youtube: '',
    instagram: '',
    facebook: ''
  });

  useEffect(() => {
    // Load social links from settings
    axios.get(`${API}/settings`)
      .then(res => {
        if (res.data.social_links) {
          setSocialLinks(res.data.social_links);
        }
      })
      .catch(() => {});
  }, []);

  const locations = [
    { name: 'Frankfurt Schwanheim', slug: 'frankfurt-schwanheim' },
    { name: 'Frankfurt Niederrad', slug: 'frankfurt-niederrad' },
    { name: 'Offenbach am Main', slug: 'offenbach' },
    { name: 'Bad Homburg', slug: 'bad-homburg' },
  ];

  const quickLinks = [
    { name: 'Führerschein Klasse B', path: '/fuehrerschein-klasse-b' },
    { name: 'Intensivkurs', path: '/intensivkurs' },
    { name: 'Ratgeber & Blog', path: '/blog' },
    { name: 'Kontakt', path: '/kontakt' },
    { name: 'Impressum', path: '/impressum' },
    { name: 'Datenschutz', path: '/datenschutz' },
  ];

  return (
    <footer className="bg-slate-950 text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>

      {/* Newsletter Section */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{c('cta_title', 'Bereit für deinen Führerschein?')}</h3>
              <p className="text-slate-400">{c('cta_subtitle', 'Vereinbare jetzt dein kostenloses Beratungsgespräch.')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
                <Button type="submit" className="h-12 px-8 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-semibold shadow-lg shadow-blue-500/20">
                  Jetzt anmelden <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
              <a href="tel:+496920326059">
                <Button variant="outline" className="h-12 px-8 border-white/20 text-white hover:bg-white/10 rounded-full">
                  <Phone className="mr-2 w-4 h-4" />
                  Anrufen
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_303cab3b-acc1-4381-b3cd-1e06f864619c/artifacts/i70vcgc8_Logo-Roessner-Hell.png"
              alt="Fahrschule Rößner"
              className="h-12 w-auto mb-6"
            />
            <p className="text-slate-400 mb-6 leading-relaxed">
              {c('beschreibung', 'Deine moderne Fahrschule in Frankfurt, Offenbach und Bad Homburg.')}
            </p>
            <div className="flex gap-3">
              {socialLinks.youtube && (
              <a 
                href={socialLinks.youtube}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-red-600 rounded-xl flex items-center justify-center transition-all duration-300"
                data-testid="footer-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              )}
              {socialLinks.instagram && (
              <a 
                href={socialLinks.instagram}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-pink-600 rounded-xl flex items-center justify-center transition-all duration-300"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              )}
              {socialLinks.facebook && (
              <a 
                href={socialLinks.facebook}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-blue-700 rounded-xl flex items-center justify-center transition-all duration-300"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              )}
            </div>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{c('standorte_title', 'Standorte')}</h4>
            <ul className="space-y-4">
              {locations.map((loc) => (
                <li key={loc.slug}>
                  <Link 
                    to={`/standorte/${loc.slug}`}
                    className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 group"
                    data-testid={`footer-location-${loc.slug}`}
                  >
                    <MapPin className="w-4 h-4 text-blue-500" />
                    <span className="group-hover:translate-x-1 transition-transform">{loc.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{c('links_title', 'Schnellzugriff')}</h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors group flex items-center gap-2"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                    <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+496920326059"
                  className="flex items-center gap-4 text-slate-400 hover:text-white transition-colors group"
                  data-testid="footer-phone"
                >
                  <div className="w-11 h-11 bg-white/5 group-hover:bg-blue-600 rounded-xl flex items-center justify-center transition-all">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Telefon</div>
                    <div className="font-medium text-white">{c('telefon', '069 20 32 60 59')}</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@roessnerfahrschule.de"
                  className="flex items-center gap-4 text-slate-400 hover:text-white transition-colors group"
                  data-testid="footer-email"
                >
                  <div className="w-11 h-11 bg-white/5 group-hover:bg-blue-600 rounded-xl flex items-center justify-center transition-all">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">E-Mail</div>
                    <div className="font-medium text-white">{c('email', 'info@roessnerfahrschule.de')}</div>
                  </div>
                </a>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-11 h-11 bg-white/5 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Öffnungszeiten</div>
                  <div className="font-medium text-white">{c('oeffnungszeiten', 'Mo-Fr: 16:00-19:00')}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} {c('copyright', 'Fahrschule Rößner GmbH. Alle Rechte vorbehalten.')}
            </p>
          </div>
        </div>
      </div>

      {/* LocalBusiness Schema for main organization */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DrivingSchool",
          "name": "Fahrschule Rößner GmbH",
          "image": "https://roessnerfahrschule.de/neu/wp-content/uploads/2025/04/cropped-Logo-Roessner.webp",
          "url": "https://roessnerfahrschule.de",
          "telephone": "+49-69-20326059",
          "email": "info@roessnerfahrschule.de",
          "description": "Fahrschule in Frankfurt, Offenbach und Bad Homburg. Führerschein Klasse B, A, Motorrad. Über 34 Jahre Erfahrung, 5.400+ bestandene Prüfungen.",
          "foundingDate": "1991",
          "numberOfEmployees": "10-20",
          "priceRange": "€€",
          "areaServed": ["Frankfurt am Main", "Offenbach am Main", "Bad Homburg", "Niederrad", "Schwanheim"],
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Alt Schwanheim 29",
            "addressLocality": "Frankfurt am Main",
            "postalCode": "60529",
            "addressRegion": "Hessen",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "50.0826",
            "longitude": "8.5977"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "16:00",
              "closes": "19:00"
            }
          ],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "127",
            "bestRating": "5"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Führerschein-Ausbildung",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Führerschein Klasse B",
                  "description": "PKW-Führerschein bis 3,5 Tonnen"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Führerschein Klasse A",
                  "description": "Motorrad-Führerschein unbegrenzt"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Führerschein Klasse A2",
                  "description": "Motorrad-Führerschein bis 35 kW"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Intensivkurs Führerschein",
                  "description": "Führerschein in 2-4 Wochen"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Begleitetes Fahren BF17",
                  "description": "Führerschein mit 17 Jahren"
                }
              }
            ]
          },
          "sameAs": [
            "https://www.facebook.com/fahrschuleroessner",
            "https://www.instagram.com/fahrschuleroessner"
          ]
        })
      }} />
    </footer>
  );
};

export default Footer;

